import React, { useState, useEffect, useRef } from 'react';
import { format, subDays } from 'date-fns';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5percent from "@amcharts/amcharts5/percent";
import * as am5radar from "@amcharts/amcharts5/radar";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { api } from '../../services/api';
import { toast } from 'react-toastify';
import {
    CubeIcon,
    CurrencyDollarIcon,
    CogIcon,
    ShoppingCartIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';

const ReportsWithCharts = () => {
    const [selectedReport, setSelectedReport] = useState('inventory-status');
    const [dateRange, setDateRange] = useState({
        start_date: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
        end_date: format(new Date(), 'yyyy-MM-dd')
    });
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [filters, setFilters] = useState({
        category: '',
        status: '',
        vendor_id: '',
        customer_id: '',
        product_id: '',
        batch_id: '',
        location_id: ''
    });

    // Chart refs
    const chartRefs = useRef({});
    const chartInstances = useRef({});

    const reportTypes = [
        {
            id: 'inventory-status',
            name: 'Inventory Status',
            description: 'Current inventory levels, stock values, and reorder suggestions',
            icon: CubeIcon
        },
        {
            id: 'sales-analysis',
            name: 'Sales Analysis',
            description: 'Sales by product, customer, and time period with profitability analysis',
            icon: CurrencyDollarIcon
        },
        {
            id: 'production-performance',
            name: 'Production Performance',
            description: 'Production batch completion rates, efficiency metrics, and output trends',
            icon: CogIcon
        },
        {
            id: 'purchasing-analysis',
            name: 'Purchasing Analysis',
            description: 'Purchase orders by supplier, parts analysis, and lead time metrics',
            icon: ShoppingCartIcon
        },
        {
            id: 'financial-summary',
            name: 'Financial Summary',
            description: 'Revenue, costs, profitability, and cash flow analysis',
            icon: ChartBarIcon
        }
    ];

    // Cleanup charts on unmount
    useEffect(() => {
        return () => {
            Object.values(chartInstances.current).forEach(chart => {
                if (chart) {
                    chart.dispose();
                }
            });
        };
    }, []);

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleGenerateReport = async () => {
        setLoading(true);
        try {
            const payload = {
                ...dateRange,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value !== '')
                )
            };

            const response = await api.post(`/reports/${selectedReport}`, payload);

            if (response.data.success) {
                setReportData(response.data.data);
                // Wait for DOM to update before creating charts
                setTimeout(() => createCharts(selectedReport, response.data.data), 100);
                toast.success('Report generated successfully');
            } else {
                toast.error('Failed to generate report');
            }
        } catch (error) {
            console.error('Error generating report:', error);
            toast.error(error.response?.data?.message || 'Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const createCharts = (reportType, data) => {
        // Dispose existing charts
        Object.values(chartInstances.current).forEach(chart => {
            if (chart) {
                chart.dispose();
            }
        });
        chartInstances.current = {};

        switch (reportType) {
            case 'inventory-status':
                createInventoryCharts(data);
                break;
            case 'sales-analysis':
                createSalesCharts(data);
                break;
            case 'production-performance':
                createProductionCharts(data);
                break;
            case 'purchasing-analysis':
                createPurchasingCharts(data);
                break;
            case 'financial-summary':
                createFinancialCharts(data);
                break;
            default:
                break;
        }
    };

    const createInventoryCharts = (data) => {
        // Category Breakdown Pie Chart
        if (chartRefs.current.categoryChart && data.category_breakdown?.length > 0) {
            const root = am5.Root.new(chartRefs.current.categoryChart);
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5percent.PieChart.new(root, {
                    layout: root.verticalLayout
                })
            );

            const series = chart.series.push(
                am5percent.PieSeries.new(root, {
                    valueField: "total_value",
                    categoryField: "category",
                    legendLabelText: "{category}",
                    legendValueText: "${value}"
                })
            );

            series.data.setAll(data.category_breakdown);
            series.appear(1000, 100);

            const legend = chart.children.push(am5.Legend.new(root, {
                centerX: am5.percent(50),
                x: am5.percent(50),
                marginTop: 15,
                marginBottom: 15
            }));
            legend.data.setAll(series.dataItems);

            chartInstances.current.categoryChart = root;
        }

        // Top Value Items Bar Chart
        if (chartRefs.current.topItemsChart && data.top_value_items?.length > 0) {
            const root = am5.Root.new(chartRefs.current.topItemsChart);
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: false,
                    wheelX: "none",
                    wheelY: "none"
                })
            );

            const yAxis = chart.yAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "name",
                    renderer: am5xy.AxisRendererY.new(root, {
                        inversed: true,
                        cellStartLocation: 0.1,
                        cellEndLocation: 0.9
                    })
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererX.new(root, {}),
                    numberFormat: "$#,###.00"
                })
            );

            const series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueXField: "total_value",
                    categoryYField: "name"
                })
            );

            series.columns.template.setAll({
                cornerRadiusBR: 5,
                cornerRadiusTR: 5
            });

            yAxis.data.setAll(data.top_value_items);
            series.data.setAll(data.top_value_items);
            series.appear(1000, 100);

            chartInstances.current.topItemsChart = root;
        }
    };

    const createSalesCharts = (data) => {
        // Sales Trend Line Chart
        if (chartRefs.current.salesTrendChart && data.sales_by_date?.length > 0) {
            const root = am5.Root.new(chartRefs.current.salesTrendChart);
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: true,
                    panY: true,
                    wheelX: "panX",
                    wheelY: "zoomX"
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.DateAxis.new(root, {
                    baseInterval: { timeUnit: "day", count: 1 },
                    renderer: am5xy.AxisRendererX.new(root, {})
                })
            );

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {}),
                    numberFormat: "$#,###.00"
                })
            );

            const series = chart.series.push(
                am5xy.LineSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "revenue",
                    valueXField: "timestamp",
                    tooltip: am5.Tooltip.new(root, {
                        labelText: "{valueY}"
                    })
                })
            );

            const processedData = data.sales_by_date.map(item => ({
                ...item,
                timestamp: new Date(item.date).getTime()
            }));

            series.data.setAll(processedData);
            series.appear(1000, 100);

            chart.set("scrollbarX", am5.Scrollbar.new(root, {
                orientation: "horizontal"
            }));

            chartInstances.current.salesTrendChart = root;
        }

        // Top Products Horizontal Bar Chart
        if (chartRefs.current.topProductsChart && data.top_products?.length > 0) {
            const root = am5.Root.new(chartRefs.current.topProductsChart);
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: false,
                    wheelX: "none",
                    wheelY: "none",
                    layout: root.verticalLayout
                })
            );

            const yAxis = chart.yAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "display_name",
                    renderer: am5xy.AxisRendererY.new(root, {
                        inversed: true,
                        cellStartLocation: 0.1,
                        cellEndLocation: 0.9
                    })
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererX.new(root, {}),
                    numberFormat: "$#,###"
                })
            );

            const series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueXField: "revenue",
                    categoryYField: "product_name"
                })
            );

            series.columns.template.setAll({
                cornerRadiusBR: 5,
                cornerRadiusTR: 5,
                strokeOpacity: 0
            });

            const topProductsData = data.top_products.slice(0, 15).map(product => ({
                ...product,
                product_name: product.product_name || `Product ${product.product_id || 'Unknown'}`
            }));

            yAxis.data.setAll(topProductsData);
            series.data.setAll(topProductsData);
            series.appear(1000, 100);

            chartInstances.current.topProductsChart = root;
        }

        // Monthly Trends Combined Chart (Revenue Columns + Order Count Line)
        if (chartRefs.current.monthlyTrendsChart && data.monthly_trends?.length > 0) {
            const root = am5.Root.new(chartRefs.current.monthlyTrendsChart);
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: false,
                    layout: root.verticalLayout
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "month",
                    renderer: am5xy.AxisRendererX.new(root, {})
                })
            );

            // Left Y-axis for revenue
            const yAxisLeft = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {}),
                    numberFormat: "$#,###"
                })
            );

            // Right Y-axis for order count
            const yAxisRight = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {
                        opposite: true
                    })
                })
            );

            // Revenue columns
            const revenueSeries = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxisLeft,
                    valueYField: "revenue",
                    categoryXField: "month",
                    name: "Revenue"
                })
            );

            revenueSeries.columns.template.setAll({
                cornerRadiusTL: 5,
                cornerRadiusTR: 5,
                fill: am5.color("#3b82f6")
            });

            // Order count line
            const orderSeries = chart.series.push(
                am5xy.LineSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxisRight,
                    valueYField: "orders_count",
                    categoryXField: "month",
                    name: "Orders"
                })
            );

            orderSeries.strokes.template.setAll({
                strokeWidth: 3,
                stroke: am5.color("#ef4444")
            });

            orderSeries.bullets.push(function() {
                return am5.Bullet.new(root, {
                    sprite: am5.Circle.new(root, {
                        radius: 4,
                        fill: am5.color("#ef4444")
                    })
                });
            });

            // Add legend
            const legend = chart.children.push(
                am5.Legend.new(root, {
                    centerX: am5.percent(50),
                    x: am5.percent(50)
                })
            );
            legend.data.setAll(chart.series.values);

            xAxis.data.setAll(data.monthly_trends);
            revenueSeries.data.setAll(data.monthly_trends);
            orderSeries.data.setAll(data.monthly_trends);

            revenueSeries.appear(1000, 100);
            orderSeries.appear(1000, 100);

            chartInstances.current.monthlyTrendsChart = root;
        }
    };

    const createProductionCharts = (data) => {
        // Daily Output Area Chart
        if (chartRefs.current.dailyOutputChart && data.daily_output?.length > 0) {
            const root = am5.Root.new(chartRefs.current.dailyOutputChart);
            root.setThemes([am5themes_Animated.new(root)]);

            // Calculate max values for proper Y-axis bounds
            const maxQuantity = Math.max(...data.daily_output.map(d => d.quantity_produced || 0));
            const maxItems = Math.max(...data.daily_output.map(d => d.items_completed || 0));
            const maxValue = Math.max(maxQuantity, maxItems);
            const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.15) : 100; // Add 15% padding

            // Ensure the root container is properly sized and clipped
            root.container.setAll({
                width: am5.percent(100),
                height: am5.percent(100),
                clipContent: true
            });

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: true,
                    panY: false,
                    wheelX: "panX",
                    wheelY: "zoomX",
                    paddingLeft: 20,
                    paddingRight: 20,
                    paddingTop: 40,
                    paddingBottom: 60,
                    clipContent: true
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.DateAxis.new(root, {
                    baseInterval: { timeUnit: "day", count: 1 },
                    renderer: am5xy.AxisRendererX.new(root, {
                        minGridDistance: 30,
                        clipContent: true
                    })
                })
            );

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {
                        clipContent: true
                    }),
                    min: 0,
                    max: yAxisMax,
                    strictMinMax: true
                })
            );

            // Quantity produced area series
            const quantitySeries = chart.series.push(
                am5xy.SmoothedXLineSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "quantity_produced",
                    valueXField: "timestamp",
                    name: "Quantity Produced",
                    clipFront: false,
                    clipBack: false
                })
            );

            // Set clipping for the series
            quantitySeries.set("clipContent", true);

            quantitySeries.fills.template.setAll({
                fillOpacity: 0.3,
                visible: true,
                fill: am5.color("#10b981")
            });

            quantitySeries.strokes.template.setAll({
                strokeWidth: 2,
                stroke: am5.color("#10b981")
            });

            // Items completed line series
            const itemsSeries = chart.series.push(
                am5xy.SmoothedXLineSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "items_completed",
                    valueXField: "timestamp",
                    name: "Batches Completed",
                    clipFront: false,
                    clipBack: false
                })
            );

            // Set clipping for the series
            itemsSeries.set("clipContent", true);

            itemsSeries.strokes.template.setAll({
                strokeWidth: 2,
                stroke: am5.color("#3b82f6"),
                strokeDasharray: [5, 5]
            });

            // Add legend with proper positioning
            const legend = chart.children.push(
                am5.Legend.new(root, {
                    centerX: am5.percent(50),
                    x: am5.percent(50),
                    marginTop: 15,
                    marginBottom: 15,
                    layout: root.horizontalLayout
                })
            );
            legend.data.setAll(chart.series.values);

            const processedData = data.daily_output.map(item => ({
                ...item,
                timestamp: new Date(item.date).getTime()
            }));

            quantitySeries.data.setAll(processedData);
            itemsSeries.data.setAll(processedData);

            quantitySeries.appear(1000, 100);
            itemsSeries.appear(1000, 100);

            chart.set("scrollbarX", am5.Scrollbar.new(root, {
                orientation: "horizontal"
            }));

            chartInstances.current.dailyOutputChart = root;
        }

        // Worker Productivity Column Chart
        if (chartRefs.current.workerChart && data.worker_productivity?.length > 0) {
            const root = am5.Root.new(chartRefs.current.workerChart);
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: true,
                    panY: true,
                    wheelX: "panX",
                    wheelY: "zoomX",
                    pinchZoomX: true,
                    paddingLeft: 0,
                    paddingRight: 1
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    maxDeviation: 0.3,
                    categoryField: "user_name",
                    renderer: am5xy.AxisRendererX.new(root, {
                        cellStartLocation: 0.1,
                        cellEndLocation: 0.9,
                        minorGridEnabled: true
                    }),
                    tooltip: am5.Tooltip.new(root, {})
                })
            );

            xAxis.get("renderer").labels.template.setAll({
                rotation: -45,
                centerY: am5.p50,
                centerX: am5.p100
            });

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {
                        strokeDasharray: [1, 3]
                    })
                })
            );

            const series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    name: "Batches Completed",
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "completed_items",
                    sequencedInterpolation: true,
                    categoryXField: "user_name",
                    tooltip: am5.Tooltip.new(root, {
                        labelText: "{categoryX}: {valueY} items"
                    })
                })
            );

            series.columns.template.setAll({
                cornerRadiusTL: 5,
                cornerRadiusTR: 5,
                strokeOpacity: 0
            });

            // Color gradient for each column
            series.columns.template.adapters.add("fill", function (fill, target) {
                return chart.get("colors").getIndex(series.columns.indexOf(target));
            });

            const topWorkers = data.worker_productivity.slice(0, 10);

            xAxis.data.setAll(topWorkers);
            series.data.setAll(topWorkers);

            series.appear(1000);
            chart.appear(1000, 100);

            chartInstances.current.workerChart = root;
        }
    };

    const createPurchasingCharts = (data) => {
        // Vendor Spending Bar Chart
        if (chartRefs.current.vendorChart && data.vendor_analysis?.length > 0) {
            const root = am5.Root.new(chartRefs.current.vendorChart);
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: false,
                    layout: root.verticalLayout
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "vendor_name",
                    renderer: am5xy.AxisRendererX.new(root, {
                        minGridDistance: 30
                    })
                })
            );
            xAxis.get("renderer").labels.template.setAll({
                rotation: -45,
                centerY: am5.p50,
                centerX: am5.p100
            });

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {}),
                    numberFormat: "$#,###"
                })
            );

            const series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "total_spend",
                    categoryXField: "vendor_name"
                })
            );

            series.columns.template.setAll({
                cornerRadiusTL: 5,
                cornerRadiusTR: 5,
                strokeOpacity: 0
            });

            xAxis.data.setAll(data.vendor_analysis.slice(0, 10));
            series.data.setAll(data.vendor_analysis.slice(0, 10));
            series.appear(1000, 100);

            chartInstances.current.vendorChart = root;
        }

        // Lead Time Analysis Chart
        if (chartRefs.current.leadTimeChart && data.lead_time_analysis?.length > 0) {
            const root = am5.Root.new(chartRefs.current.leadTimeChart);
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: false,
                    layout: root.verticalLayout
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "vendor",
                    renderer: am5xy.AxisRendererX.new(root, {})
                })
            );

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {}),
                    min: 0
                })
            );

            const series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "average_lead_time",
                    categoryXField: "vendor",
                    name: "Average Lead Time"
                })
            );

            series.columns.template.setAll({
                cornerRadiusTL: 5,
                cornerRadiusTR: 5
            });

            xAxis.data.setAll(data.lead_time_analysis);
            series.data.setAll(data.lead_time_analysis);
            series.appear(1000, 100);

            chartInstances.current.leadTimeChart = root;
        }
    };

    const createFinancialCharts = (data) => {
        // Monthly P&L Trend Chart
        if (chartRefs.current.profitTrendChart && data.monthly_trends?.length > 0) {
            const root = am5.Root.new(chartRefs.current.profitTrendChart);
            root.setThemes([am5themes_Animated.new(root)]);

            // Calculate max values for proper Y-axis bounds
            const maxRevenue = Math.max(...data.monthly_trends.map(d => d.revenue || 0));
            const maxCogs = Math.max(...data.monthly_trends.map(d => d.cogs || 0));
            const maxGrossProfit = Math.max(...data.monthly_trends.map(d => d.gross_profit || 0));
            const maxNetProfit = Math.max(...data.monthly_trends.map(d => d.net_profit || 0));
            const minNetProfit = Math.min(...data.monthly_trends.map(d => d.net_profit || 0));

            const maxValue = Math.max(maxRevenue, maxCogs, maxGrossProfit, maxNetProfit);
            const minValue = Math.min(0, minNetProfit); // Allow for negative net profit
            const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 100000;
            const yAxisMin = minValue < 0 ? Math.floor(minValue * 1.1) : 0;

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: false,
                    layout: root.verticalLayout,
                    paddingTop: 20,
                    paddingRight: 20,
                    paddingBottom: 20,
                    paddingLeft: 20
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "month",
                    renderer: am5xy.AxisRendererX.new(root, {
                        minGridDistance: 30
                    })
                })
            );

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {}),
                    numberFormat: "$#,###",
                    min: yAxisMin,
                    max: yAxisMax,
                    strictMinMax: true
                })
            );

            // Revenue series (column)
            const revenueSeries = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "revenue",
                    categoryXField: "month",
                    name: "Revenue",
                    fill: am5.color("#3b82f6"),
                    tooltip: am5.Tooltip.new(root, {
                        labelText: "[bold]{name}[/]\nMonth: {categoryX}\nRevenue: ${valueY}\nOrders: {orders_count}"
                    })
                })
            );

            // COGS series (stacked column)
            const cogsSeries = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "cogs",
                    categoryXField: "month",
                    name: "COGS",
                    fill: am5.color("#ef4444"),
                    tooltip: am5.Tooltip.new(root, {
                        labelText: "[bold]{name}[/]\nMonth: {categoryX}\nCOGS: ${valueY}\nGross Margin: {gross_margin}%"
                    })
                })
            );

            // Gross profit line
            const grossProfitSeries = chart.series.push(
                am5xy.LineSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "gross_profit",
                    categoryXField: "month",
                    name: "Gross Profit",
                    stroke: am5.color("#10b981"),
                    tooltip: am5.Tooltip.new(root, {
                        labelText: "[bold]{name}[/]\nMonth: {categoryX}\nGross Profit: ${valueY}\nGross Margin: {gross_margin}%\nRevenue: ${revenue}\nCOGS: ${cogs}"
                    })
                })
            );

            // Net profit line
            const netProfitSeries = chart.series.push(
                am5xy.LineSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "net_profit",
                    categoryXField: "month",
                    name: "Net Profit",
                    stroke: am5.color("#8b5cf6"),
                    tooltip: am5.Tooltip.new(root, {
                        labelText: "[bold]{name}[/]\nMonth: {categoryX}\nNet Profit: ${valueY}\nNet Margin: {net_margin}%\nGross Profit: ${gross_profit}\nOperating Expenses: ${operating_expenses}"
                    })
                })
            );

            grossProfitSeries.strokes.template.setAll({
                strokeWidth: 3
            });

            netProfitSeries.strokes.template.setAll({
                strokeWidth: 3,
                strokeDasharray: [5, 5]
            });

            // Add legend
            const legend = chart.children.push(
                am5.Legend.new(root, {
                    centerX: am5.percent(50),
                    x: am5.percent(50)
                })
            );
            legend.data.setAll(chart.series.values);

            xAxis.data.setAll(data.monthly_trends);
            revenueSeries.data.setAll(data.monthly_trends);
            cogsSeries.data.setAll(data.monthly_trends);
            grossProfitSeries.data.setAll(data.monthly_trends);
            netProfitSeries.data.setAll(data.monthly_trends);

            revenueSeries.appear(1000, 100);
            cogsSeries.appear(1000, 100);
            grossProfitSeries.appear(1000, 100);
            netProfitSeries.appear(1000, 100);

            chartInstances.current.profitTrendChart = root;
        }

        // Product Profitability Chart
        if (chartRefs.current.productProfitChart && data.product_profitability?.length > 0) {
            const root = am5.Root.new(chartRefs.current.productProfitChart);
            root.setThemes([am5themes_Animated.new(root)]);

            // Use SKU directly as category - no aggregation needed
            const topProducts = data.product_profitability
                .map(product => ({
                    sku: product.sku,
                    product_name: product.product_name,
                    display_name: product.sku, // Use SKU as display name
                    profit: parseFloat(product.profit || 0),
                    revenue: parseFloat(product.revenue || 0),
                    cogs: parseFloat(product.cogs || 0),
                    margin: parseFloat(product.margin || 0),
                    units_sold: parseInt(product.units_sold || 0)
                }))
                .sort((a, b) => b.profit - a.profit)
                .slice(0, 100);

            console.log('Top 20 products by SKU:', topProducts);

            // Calculate max/min values for proper X-axis bounds
            const profits = topProducts.map(d => d.profit).filter(p => !isNaN(p));
            const maxProfit = profits.length > 0 ? Math.max(...profits) : 0;
            const minProfit = profits.length > 0 ? Math.min(...profits) : 0;
            const xAxisMax = maxProfit > 0 ? Math.ceil(maxProfit * 1.1) : 1000;
            const xAxisMin = minProfit < 0 ? Math.floor(minProfit * 1.1) : 0;

            console.log('Profit range:', { minProfit, maxProfit, xAxisMin, xAxisMax });

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: true,
                    wheelX: "none",
                    wheelY: "zoomY",
                    paddingTop: 20,
                    paddingRight: 20,
                    paddingBottom: 20,
                    paddingLeft: 20,
                    layout: root.verticalLayout
                })
            );

            const yAxis = chart.yAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "display_name",
                    renderer: am5xy.AxisRendererY.new(root, {
                        inversed: true,
                        cellStartLocation: 0.1,
                        cellEndLocation: 0.9,
                        minGridDistance: 20,
                        maxZoomCount: 50
                    })
                })
            );

            const xAxis = chart.xAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererX.new(root, {}),
                    numberFormat: "$#,###",
                    min: xAxisMin,
                    max: xAxisMax,
                    strictMinMax: true
                })
            );

            // Profit bars
            const profitSeries = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueXField: "profit",
                    categoryYField: "display_name",
                    name: "Profit"
                })
            );

            profitSeries.columns.template.setAll({
                cornerRadiusBR: 5,
                cornerRadiusTR: 5,
                strokeOpacity: 0,
                fill: am5.color("#10b981"),
                tooltipText: "[bold]{categoryY}[/]\nProfit: ${valueX}\nRevenue: ${revenue}\nCOGS: ${cogs}\nUnits Sold: {units_sold}\nMargin: {margin}%",
                width: am5.percent(80) // Ensure bars have proper width
            });

            // Color coding for positive/negative profit
            profitSeries.columns.template.adapters.add("fill", (fill, target) => {
                const value = target.dataItem?.get("valueX") || 0;
                if (value < 0) {
                    return am5.color("#ef4444"); // Red for losses
                } else if (value === 0) {
                    return am5.color("#94a3b8"); // Gray for zero profit
                }
                return am5.color("#10b981"); // Green for profits
            });

            // Add visibility adapter to ensure all bars show
            profitSeries.columns.template.adapters.add("visible", (visible, target) => {
                return true; // Force all bars to be visible
            });

            // Set data first
            yAxis.data.setAll(topProducts);
            profitSeries.data.setAll(topProducts);

            // Debug: Check data before and after setting
            console.log('Final topProducts before chart:', topProducts.map(p => ({ display_name: p.display_name, profit: p.profit })));

            // Check for duplicates
            const displayNames = topProducts.map(p => p.display_name);
            const uniqueNames = [...new Set(displayNames)];
            console.log('Display names:', displayNames.length, 'Unique names:', uniqueNames.length);

            setTimeout(() => {
                console.log('Series data items:', profitSeries.dataItems.length);
                profitSeries.dataItems.forEach((item, index) => {
                    console.log(`Item ${index}:`, {
                        category: item.get("categoryY"),
                        value: item.get("valueX"),
                        visible: item.get("visible")
                    });
                });
            }, 100);

            // Add scrollbar for better navigation with more products
            chart.set("scrollbarY", am5.Scrollbar.new(root, {
                orientation: "vertical",
                marginRight: 20
            }));

            // Force the chart to show all categories initially
            yAxis.zoom(0, 1);

            profitSeries.appear(1000, 100);

            chartInstances.current.productProfitChart = root;
        }
    };

    const renderFilterOptions = () => {
        switch (selectedReport) {
            case 'inventory-status':
                return (
                    <>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Category
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                            >
                                <option value="">All Categories</option>
                                <option value="RAW">Raw Materials</option>
                                <option value="COMPONENT">Components</option>
                                <option value="PRODUCT">Finished Products</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Stock Status
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={filters.low_stock_only}
                                onChange={(e) => handleFilterChange('low_stock_only', e.target.value)}
                            >
                                <option value="">All Items</option>
                                <option value="true">Low Stock Only</option>
                            </select>
                        </div>
                    </>
                );
            case 'sales-analysis':
                return (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Order Status
                        </label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                );
            case 'production-performance':
                return (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Batch Status
                        </label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="planned">Planned</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                );
            case 'purchasing-analysis':
                return (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Order Status
                        </label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="ordered">Ordered</option>
                            <option value="partial">Partially Received</option>
                            <option value="received">Received</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                );
            default:
                return null;
        }
    };

    const renderReportContent = () => {
        if (!reportData) {
            return (
                <div className="text-center py-12 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="mt-2">Select filters and generate a report to view data</p>
                </div>
            );
        }

        const { summary } = reportData;

        return (
            <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {selectedReport === 'inventory-status' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Total Items</div>
                                <div className="text-2xl font-bold">{summary.total_items}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Total Value</div>
                                <div className="text-2xl font-bold">${parseFloat(summary.total_value || 0).toLocaleString()}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Low Stock Items</div>
                                <div className="text-2xl font-bold text-red-600">{summary.low_stock_items}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Parts Value</div>
                                <div className="text-2xl font-bold">${parseFloat(summary.total_parts_value || 0).toLocaleString()}</div>
                            </div>
                        </>
                    )}

                    {selectedReport === 'sales-analysis' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Total Orders</div>
                                <div className="text-2xl font-bold">{summary.total_orders}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Total Revenue</div>
                                <div className="text-2xl font-bold">${parseFloat(summary.total_revenue || 0).toLocaleString()}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Avg Order Value</div>
                                <div className="text-2xl font-bold">${parseFloat(summary.average_order_value || 0).toFixed(2)}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Unique Customers</div>
                                <div className="text-2xl font-bold">{summary.unique_customers}</div>
                            </div>
                        </>
                    )}

                    {selectedReport === 'production-performance' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Total Batches</div>
                                <div className="text-2xl font-bold">{summary.total_batches}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Completed</div>
                                <div className="text-2xl font-bold text-green-600">{summary.completed_batches}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Avg Completion</div>
                                <div className="text-2xl font-bold">{summary.average_completion}%</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">On-Time Rate</div>
                                <div className="text-2xl font-bold">{summary.on_time_delivery_rate}%</div>
                            </div>
                        </>
                    )}

                    {selectedReport === 'purchasing-analysis' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Total Orders</div>
                                <div className="text-2xl font-bold">{summary.total_orders}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Total Spend</div>
                                <div className="text-2xl font-bold">${parseFloat(summary.total_spend || 0).toLocaleString()}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Avg Order Value</div>
                                <div className="text-2xl font-bold">${parseFloat(summary.average_order_value || 0).toFixed(2)}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Unique Vendors</div>
                                <div className="text-2xl font-bold">{summary.unique_vendors}</div>
                            </div>
                        </>
                    )}

                    {selectedReport === 'financial-summary' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Revenue</div>
                                <div className="text-2xl font-bold">${parseFloat(summary.revenue || 0).toLocaleString()}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Gross Profit</div>
                                <div className="text-2xl font-bold text-green-600">${parseFloat(summary.gross_profit || 0).toLocaleString()}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Gross Margin</div>
                                <div className="text-2xl font-bold">{summary.gross_margin}%</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-sm text-gray-500">Inventory Value</div>
                                <div className="text-2xl font-bold">${parseFloat(summary.inventory_value || 0).toLocaleString()}</div>
                            </div>
                        </>
                    )}
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {selectedReport === 'inventory-status' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="text-lg font-semibold mb-4">Category Distribution</h3>
                                <div ref={(el) => chartRefs.current.categoryChart = el} style={{ height: '300px' }}></div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="text-lg font-semibold mb-4">Top Value Items</h3>
                                <div ref={(el) => chartRefs.current.topItemsChart = el} style={{ height: '300px' }}></div>
                            </div>
                        </>
                    )}

                    {selectedReport === 'sales-analysis' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow col-span-2">
                                <h3 className="text-lg font-semibold mb-4">Sales Trend</h3>
                                <div ref={(el) => chartRefs.current.salesTrendChart = el} style={{ height: '300px' }}></div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="text-lg font-semibold mb-4">Top Products</h3>
                                <div ref={(el) => chartRefs.current.topProductsChart = el} style={{ height: '300px' }}></div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="text-lg font-semibold mb-4">Monthly Trends</h3>
                                <div ref={(el) => chartRefs.current.monthlyTrendsChart = el} style={{ height: '300px' }}></div>
                            </div>
                        </>
                    )}

                    {selectedReport === 'production-performance' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="text-lg font-semibold mb-4">Daily Output</h3>
                                <div ref={(el) => chartRefs.current.dailyOutputChart = el} style={{ height: '400px', width: '100%', overflow: 'hidden' }}></div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="text-lg font-semibold mb-4">Worker Productivity</h3>
                                <div ref={(el) => chartRefs.current.workerChart = el} style={{ height: '300px' }}></div>
                            </div>
                        </>
                    )}

                    {selectedReport === 'purchasing-analysis' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="text-lg font-semibold mb-4">Vendor Spending</h3>
                                <div ref={(el) => chartRefs.current.vendorChart = el} style={{ height: '300px' }}></div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="text-lg font-semibold mb-4">Lead Time Analysis</h3>
                                <div ref={(el) => chartRefs.current.leadTimeChart = el} style={{ height: '300px' }}></div>
                            </div>
                        </>
                    )}

                    {selectedReport === 'financial-summary' && (
                        <>
                            <div className="bg-white p-4 rounded-lg shadow col-span-2">
                                <h3 className="text-lg font-semibold mb-4">Monthly P&L Trend</h3>
                                <div ref={(el) => chartRefs.current.profitTrendChart = el} style={{ height: '300px' }}></div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow col-span-2">
                                <h3 className="text-lg font-semibold mb-4">Product Profitability</h3>
                                <div ref={(el) => chartRefs.current.productProfitChart = el} style={{ height: '600px' }}></div>
                            </div>
                        </>
                    )}
                </div>

                {/* Data Tables */}
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold">Detailed Data</h3>
                    </div>
                    <div className="p-6">
                        {renderDataTable()}
                    </div>
                </div>
            </div>
        );
    };

    const renderDataTable = () => {
        if (!reportData) return null;

        switch (selectedReport) {
            case 'inventory-status':
                return (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Name</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Type</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Threshold</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {reportData.parts_inventory?.map((item, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-2 text-sm">{item.sku}</td>
                                        <td className="px-4 py-2 text-sm">{item.part_name}</td>
                                        <td className="px-4 py-2 text-sm">{item.part_type}</td>
                                        <td className="px-4 py-2 text-sm">{item.quantity}</td>
                                        <td className="px-4 py-2 text-sm">{item.threshold}</td>
                                        <td className="px-4 py-2 text-sm">${parseFloat(item.total_value || 0).toFixed(2)}</td>
                                        <td className="px-4 py-2 text-sm">
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                item.status === 'Low Stock' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                            }`}>
                                                {item.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );

            case 'sales-analysis':
                return (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity Sold</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {reportData.top_products?.map((product, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-2 text-sm">{product.product_name}</td>
                                        <td className="px-4 py-2 text-sm">{product.product_sku}</td>
                                        <td className="px-4 py-2 text-sm">{product.quantity_sold}</td>
                                        <td className="px-4 py-2 text-sm">${parseFloat(product.revenue || 0).toFixed(2)}</td>
                                        <td className="px-4 py-2 text-sm">{product.orders_count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Reports & Analytics</h1>

            {/* Report Selection */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">Select Report Type</h2>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                        {reportTypes.map(report => (
                            <div
                                key={report.id}
                                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                                    selectedReport === report.id
                                        ? 'border-blue-500 bg-blue-50 shadow-md'
                                        : 'border-gray-200 hover:border-blue-300 hover:shadow'
                                }`}
                                onClick={() => setSelectedReport(report.id)}
                            >
                                <div className="flex items-start">
                                    <div className="flex-shrink-0 mr-3">
                                        <report.icon className="h-6 w-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-gray-900">{report.name}</h3>
                                        <p className="text-sm text-gray-500 mt-1">{report.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Start Date
                            </label>
                            <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={dateRange.start_date}
                                onChange={(e) => handleDateChange('start_date', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                End Date
                            </label>
                            <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                value={dateRange.end_date}
                                onChange={(e) => handleDateChange('end_date', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        {renderFilterOptions()}
                    </div>

                    {/* Generate Button */}
                    <div className="flex justify-end">
                        <button
                            onClick={handleGenerateReport}
                            disabled={loading}
                            className={`px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
                                loading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Generating...
                                </span>
                            ) : 'Generate Report'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Report Content */}
            <div className="bg-gray-50 rounded-lg p-6">
                {renderReportContent()}
            </div>
        </div>
    );
};

export default ReportsWithCharts;
